import {
  ADMIN_LIFECYCLE_ACTIONS,
  KNOWN_LIFECYCLE_CODES,
  LEGACY_DELETION_PENDING_CODE,
  LIFECYCLE_CODES,
  LIFECYCLE_STATUSES,
  RECOVER_SCREEN_STATES,
} from './lifecycle.constants'

export function unwrapHttp(result) {
  if (!result || typeof result !== 'object') return { httpStatus: 0, data: {} }
  if (result.response && typeof result.response === 'object') {
    return unwrapHttp(result.response)
  }
  const body = result.data
  const looksLikeAxios = body !== undefined && typeof result.status === 'number'
  const looksLikeEnvelope = body && typeof body === 'object' && !Array.isArray(body) && (body.code || body.error || body.message || body.msg || body.status !== undefined)
  if (looksLikeAxios || looksLikeEnvelope) {
    return {
      httpStatus: typeof result.status === 'number' ? result.status : 0,
      data: body && typeof body === 'object' ? body : {},
    }
  }
  return { httpStatus: typeof result.status === 'number' ? result.status : 0, data: result }
}

function asKnownCode(value) {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (!KNOWN_LIFECYCLE_CODES.has(trimmed)) return null
  if (trimmed === LEGACY_DELETION_PENDING_CODE) return LIFECYCLE_CODES.DELETION_PENDING
  return trimmed
}

/**
 * Visual state is decided only from `code` (or a dedicated error-code field).
 * Never compare `message`. Never use `deleted_at`.
 */
export function extractLifecycleCode(errOrRes) {
  const { httpStatus, data } = unwrapHttp(errOrRes)
  const nestedError = data.error
  const candidates = [
    data.code,
    nestedError && typeof nestedError === 'object' ? nestedError.code : null,
    typeof nestedError === 'string' ? nestedError : null,
    data.lifecycle_code,
    data.lifecycle_status,
    data.error_code,
  ]
  for (const candidate of candidates) {
    const code = asKnownCode(candidate)
    if (code) return code
  }
  if (httpStatus === 429) return 'http_429'
  return null
}

export function extractLifecycleMessage(errOrRes) {
  const { data } = unwrapHttp(errOrRes)
  const nested = data.error
  if (nested && typeof nested === 'object' && nested.message) return String(nested.message)
  if (typeof data.message === 'string' && data.message.trim()) return data.message
  if (typeof data.msg === 'string' && data.msg.trim()) return data.msg
  return ''
}

export function parseLifecycleResponse(result) {
  const { httpStatus, data } = unwrapHttp(result)
  const code = extractLifecycleCode({ status: httpStatus, data })
  const message = extractLifecycleMessage({ status: httpStatus, data })
  const explicitFailure = data.status === false
  // Success payloads may reuse lifecycle codes (e.g. account_deactivated on 200).
  const ok = httpStatus >= 200 && httpStatus < 300 && !explicitFailure
  return {
    ok,
    httpStatus,
    code,
    message,
    data,
    subscriptionActionRequired: code === LIFECYCLE_CODES.SUBSCRIPTION_ACTION_REQUIRED,
    companyDeletionNotSupported: code === LIFECYCLE_CODES.COMPANY_DELETION_NOT_SUPPORTED,
  }
}

export function isLifecycleErrorCode(code) {
  return Boolean(code && KNOWN_LIFECYCLE_CODES.has(code))
}

export function httpStatusHint(httpStatus) {
  if (httpStatus === 401) return 'unauthorized'
  if (httpStatus === 404) return 'not_found'
  if (httpStatus === 409) return 'conflict'
  if (httpStatus === 422) return 'unprocessable'
  if (httpStatus === 429) return 'rate_limited'
  return null
}

export function recoverConfirmState(parsed) {
  if (!parsed) return RECOVER_SCREEN_STATES.TEMPORARY
  if (parsed.ok) return RECOVER_SCREEN_STATES.SUCCESS
  if (parsed.code === LIFECYCLE_CODES.ACCOUNT_SUSPENDED) return RECOVER_SCREEN_STATES.SUSPENDED
  if (parsed.code === LIFECYCLE_CODES.INVALID_OR_EXPIRED_REACTIVATION_TOKEN) return RECOVER_SCREEN_STATES.EXPIRED
  if (parsed.httpStatus === 401 || parsed.httpStatus === 404 || parsed.httpStatus === 422) return RECOVER_SCREEN_STATES.EXPIRED
  return RECOVER_SCREEN_STATES.TEMPORARY
}

export function canSendRecoveryLink(code) {
  return (
    code === LIFECYCLE_CODES.ACCOUNT_REACTIVATION_REQUIRED ||
    code === LIFECYCLE_CODES.ACCOUNT_DEACTIVATED ||
    code === LIFECYCLE_CODES.DELETION_PENDING
  )
}

export function canSelfReactivate(code) {
  return canSendRecoveryLink(code)
}

function firstKnownStatus(value) {
  const raw = String(value || '').trim().toLowerCase()
  if (!raw) return null
  if (raw === LIFECYCLE_CODES.ACCOUNT_SUSPENDED || raw === LIFECYCLE_STATUSES.SUSPENDED) return LIFECYCLE_STATUSES.SUSPENDED
  if (raw === LIFECYCLE_CODES.ACCOUNT_DEACTIVATED || raw === LIFECYCLE_STATUSES.DEACTIVATED) return LIFECYCLE_STATUSES.DEACTIVATED
  if (raw === LIFECYCLE_CODES.DELETION_PENDING || raw === LEGACY_DELETION_PENDING_CODE || raw === 'pending_deletion') {
    return LIFECYCLE_STATUSES.DELETION_PENDING
  }
  if (raw === LIFECYCLE_STATUSES.ACTIVE || raw === 'account_active') return LIFECYCLE_STATUSES.ACTIVE
  return null
}

export function resolveCompanyLifecycleStatus(row = {}) {
  const candidates = [
    row.lifecycle_status,
    row.lifecycleStatus,
    row.lifecycle_code,
    row.lifecycleCode,
    row.account_lifecycle,
    row.accountLifecycle,
    row.lifecycle?.code,
    row.lifecycle?.status,
    row.status_code,
    row.statusCode,
  ]
  for (const candidate of candidates) {
    const status = firstKnownStatus(candidate)
    if (status) return status
  }
  if (row.company_deletion_pending || row.companyDeletionPending || row.user_deletion_pending || row.userDeletionPending) {
    return LIFECYCLE_STATUSES.DELETION_PENDING
  }
  if (row.suspended === true || row.is_suspended === true) return LIFECYCLE_STATUSES.SUSPENDED
  if (row.deactivated === true || row.is_deactivated === true) return LIFECYCLE_STATUSES.DEACTIVATED
  const nestedCode = asKnownCode(row.code)
  if (nestedCode === LIFECYCLE_CODES.ACCOUNT_SUSPENDED) return LIFECYCLE_STATUSES.SUSPENDED
  if (nestedCode === LIFECYCLE_CODES.ACCOUNT_DEACTIVATED) return LIFECYCLE_STATUSES.DEACTIVATED
  if (nestedCode === LIFECYCLE_CODES.DELETION_PENDING) return LIFECYCLE_STATUSES.DELETION_PENDING
  return LIFECYCLE_STATUSES.ACTIVE
}

export function resolveLifecycleActions(row = {}) {
  const fromApi = row.allowed_actions || row.lifecycle_actions || row.allowedActions || row.lifecycle?.allowed_actions
  if (Array.isArray(fromApi) && fromApi.length) {
    const set = new Set(fromApi.map((action) => String(action).toLowerCase()))
    return {
      canDeactivate: set.has(ADMIN_LIFECYCLE_ACTIONS.DEACTIVATE),
      canReactivate: set.has(ADMIN_LIFECYCLE_ACTIONS.REACTIVATE),
      canSuspend: set.has(ADMIN_LIFECYCLE_ACTIONS.SUSPEND),
      canUnsuspend: set.has(ADMIN_LIFECYCLE_ACTIONS.UNSUSPEND),
    }
  }
  const status = resolveCompanyLifecycleStatus(row)
  return {
    canDeactivate: status === LIFECYCLE_STATUSES.ACTIVE,
    canReactivate: status === LIFECYCLE_STATUSES.DEACTIVATED || status === LIFECYCLE_STATUSES.DELETION_PENDING,
    canSuspend: status === LIFECYCLE_STATUSES.ACTIVE,
    canUnsuspend: status === LIFECYCLE_STATUSES.SUSPENDED,
  }
}

export function lifecycleStatusLabelKey(status) {
  if (status === LIFECYCLE_STATUSES.DEACTIVATED) return 'lifecycle_badge_deactivated'
  if (status === LIFECYCLE_STATUSES.SUSPENDED) return 'lifecycle_badge_suspended'
  if (status === LIFECYCLE_STATUSES.DELETION_PENDING) return 'lifecycle_badge_deletion_pending'
  return 'lifecycle_badge_active'
}

export function lifecycleStatusBadgeVariant(status) {
  if (status === LIFECYCLE_STATUSES.DEACTIVATED) return 'muted'
  if (status === LIFECYCLE_STATUSES.SUSPENDED) return 'danger'
  if (status === LIFECYCLE_STATUSES.DELETION_PENDING) return 'warning'
  return 'success'
}

export function queryToken(search) {
  try {
    return new URLSearchParams(search || '').get('token') || ''
  } catch {
    return ''
  }
}
